import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Project APIs
export const projectAPI = {
  getAll: () => api.get('/projects'),
  getOne: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  getStats: (id) => api.get(`/projects/${id}/stats`),
};

// Task APIs
export const taskAPI = {
  getAll: (projectId) => api.get(`/tasks?project_id=${projectId}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
};

// Expense APIs
export const expenseAPI = {
  getAll: (projectId) => api.get(`/expenses?project_id=${projectId}`),
  create: (data) => api.post('/expenses', data),
  delete: (id) => api.delete(`/expenses/${id}`),
};

// Photo APIs
export const photoAPI = {
  getAll: (projectId) => api.get(`/photos?project_id=${projectId}`),
  getOne: (id) => api.get(`/photos/${id}`),
  upload: (formData) => api.post('/photos', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => api.delete(`/photos/${id}`),
};

// Comps APIs
export const compAPI = {
  getAll: (projectId) => api.get(`/comps?project_id=${projectId}`),
  create: (data) => api.post('/comps', data),
  delete: (id) => api.delete(`/comps/${id}`),
  generate: (projectId, numComps = 5) => api.post('/comps/generate', { project_id: projectId, num_comps: numComps }),
};

// Permits APIs
export const permitAPI = {
  getAll: (projectId) => api.get(`/permits?project_id=${projectId}`),
  create: (data) => api.post('/permits', data),
  update: (id, data) => api.put(`/permits/${id}`, data),
  delete: (id) => api.delete(`/permits/${id}`),
  uploadDocument: (permitId, formData) => api.post(`/permits/${permitId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getDocuments: (permitId) => api.get(`/permits/${permitId}/documents`),
  deleteDocument: (docId) => api.delete(`/permits/documents/${docId}`),
};

// Contractor Portal APIs
export const contractorPortalAPI = {
  getAll: (projectId) => api.get(`/contractor-portal?project_id=${projectId}`),
  create: (data) => api.post('/contractor-portal', data),
  toggle: (portalId) => api.put(`/contractor-portal/${portalId}/toggle`),
  delete: (portalId) => api.delete(`/contractor-portal/${portalId}`),
  verify: (portalId, code) => api.get(`/contractor/${portalId}/verify?code=${code}`),
  getUploads: (projectId) => api.get(`/contractor-uploads?project_id=${projectId}`),
  getUpload: (uploadId) => api.get(`/contractor-uploads/${uploadId}`),
  deleteUpload: (uploadId) => api.delete(`/contractor-uploads/${uploadId}`),
};

// Contractor Accounts APIs
export const contractorAccountAPI = {
  getAll: (projectId) => api.get(`/contractor-accounts?project_id=${projectId}`),
  create: (data) => api.post('/contractor-accounts', data),
  toggle: (accountId) => api.put(`/contractor-accounts/${accountId}/toggle`),
  delete: (accountId) => api.delete(`/contractor-accounts/${accountId}`),
  resetPassword: (accountId) => api.put(`/contractor-accounts/${accountId}/reset-password`),
};

// Contractor Auth APIs (for contractor login)
export const contractorAuthAPI = {
  login: (data) => api.post('/contractor-auth/login', data),
  upload: (formData, token) => api.post('/contractor-auth/upload', formData, {
    headers: { 
      'Content-Type': 'multipart/form-data',
      'Authorization': `Bearer ${token}`
    },
  }),
};

// Contractor Upload API (no auth required - uses access code)
export const contractorUploadAPI = {
  verify: (portalId, code) => 
    axios.get(`${API_BASE}/contractor/${portalId}/verify?code=${code}`),
  upload: (portalId, formData) => 
    axios.post(`${API_BASE}/contractor/${portalId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// Market APIs
export const marketAPI = {
  search: (address, city, state) => 
    api.get(`/market/search?address=${encodeURIComponent(address)}&city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}`),
};

// Notification APIs
export const notificationAPI = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// AI APIs
export const aiAPI = {
  getInsights: (projectId, question) => 
    api.post('/ai/insights', { project_id: projectId, question }),
  generateComps: (data) => api.post('/ai/comps/search', data),
  searchComps: (data) => api.post('/ai/comps/search', data),
};

// Underwriting APIs
export const underwritingAPI = {
  quickUnderwrite: (data) => api.post('/underwrite/quick', data),
  detailedUnderwrite: (data) => api.post('/underwrite/detailed', data),
  manualUnderwrite: (data) => api.post('/underwrite/manual', data),
  getAll: () => api.get('/underwritings'),
  getOne: (id) => api.get(`/underwritings/${id}`),
  delete: (id) => api.delete(`/underwritings/${id}`),
  convertToProject: (id) => api.post(`/underwrite/${id}/convert-to-project`),
};

// Report APIs
export const reportAPI = {
  getMarketAnalysisPdf: (projectId) => api.get(`/projects/${projectId}/report/pdf`, { responseType: 'blob' }),
  downloadPDF: (projectId) => api.get(`/projects/${projectId}/report/pdf`, { responseType: 'blob' }),
  getPreview: (projectId) => api.get(`/projects/${projectId}/report/preview`),
  generateShareable: (projectId) => api.post(`/reports/generate-shareable/${projectId}`),
  getMyShared: () => api.get('/reports/my-shared'),
  deleteShared: (reportId) => api.delete(`/reports/shared/${reportId}`),
};

// Dashboard API
export const dashboardAPI = {
  getSummary: () => api.get('/dashboard/summary'),
  getFinancial: () => api.get('/dashboard/financial'),
};

// Document Vault API
export const documentAPI = {
  upload: (formData) => api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getAll: (projectId) => api.get(`/documents?project_id=${projectId}`),
  getOne: (documentId) => api.get(`/documents/${documentId}`),
  download: (documentId) => api.get(`/documents/${documentId}/download`, { responseType: 'blob' }),
  delete: (documentId) => api.delete(`/documents/${documentId}`),
  getCategories: () => api.get('/documents/categories/list'),
};

// Property Search API
export const propertyAPI = {
  search: (data) => api.post('/property/search', data),
  getTypes: () => api.get('/property/types'),
};

export default api;
